import { useEffect, useState, useCallback, useRef } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import styled from "styled-components";

import * as actions from "@/redux/actions";
import aiClient from "@apis/aiClient";
import SntDeleteIcon from "@icons/SntDeleteIcon";
import AwesomeIcon from "@icons/AwesomeIcon";
import { faArrowUp, faBars, faPlus, faStop } from "@fortawesome/free-solid-svg-icons";
import { AiDisclaimer, AiProgressUpdates, useAiProgress, ChatBubble, ToolExecution } from "@widgets/ai";
import FloatingWindow from "@widgets/ai/FloatingWindow";
import AiLoadingIcon from "@/cleanup/AiSummary/images/ai_loading_icon.svg";
import SntDialog from "@/components/SntDialog/SntDialog";
import type { AiChatConversation, ChatMessage, AiError } from "@apis/aiClient/aiClient.dto";
import type { RootState } from "@/types/RootState";
import { translate } from "@/hooks/useLanguage";
import { hasPermission } from "@/hooks/usePermissions";
import { UserPermission } from "@/types/generated/UserPermission";
import { LanguageKey } from "@/types/generated/LanguageKey";

const StyledChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
    display: flex;
    flex-direction: column;
  }

  .chat-input-container {
    padding: 0.5rem 1rem 0.5rem 1rem;
    border-top: 1px solid var(--bs-border-color);
    background-color: var(--bs-body-bg);

    .ai-disclaimer {
      margin-top: 0.5rem;
    }

    .input-wrapper {
      position: relative;
      border: 1px solid var(--bs-border-color);
      border-radius: 0.5rem;
      background-color: var(--bs-body-bg);

      &:focus-within {
        border-color: var(--bs-primary);
        box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.15);
      }

      textarea {
        border: none;
        background: transparent;
        resize: none;
        padding: 0.75rem;
        padding-bottom: 2.5rem;
        width: 100%;
        min-height: 80px;

        &:focus {
          outline: none;
          box-shadow: none;
        }
      }

      .input-actions {
        position: absolute;
        bottom: 0.5rem;
        right: 0.5rem;
        display: flex;
        gap: 0.5rem;
      }

      .send-button {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: none;
        background: linear-gradient(135deg, var(--sensolus-ai-accent-secondary) 0%, var(--sensolus-ai-accent) 100%);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        font-size: 14px;
        padding: 0;

        &:hover:not(:disabled) {
          opacity: 0.9;
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }

      .stop-button {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: none;
        background: var(--bs-danger);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        font-size: 12px;
        padding: 0;

        &:hover {
          opacity: 0.9;
        }
      }
    }
  }

  .stream-chunk {
    opacity: 0.8;
  }
`;

const HeaderButton = styled.button`
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: var(--bs-body-color);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background-color: var(--bs-light);
  }
`;

const NewChatButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #ec4899 0%, #a855f7 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  padding: 0;

  &:hover {
    opacity: 0.9;
  }
`;

const ConversationPanel = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 280px;
  background-color: var(--bs-body-bg);
  border-right: 1px solid var(--bs-border-color);
  transform: translateX(${(props) => (props.$isOpen ? "0" : "-100%")});
  transition: transform 0.3s ease-in-out;
  z-index: 1050;
  display: flex;
  flex-direction: column;

  .new-chat-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    cursor: pointer;
    color: var(--sensolus-ai-accent);
    font-weight: 500;
    border-bottom: 1px solid var(--bs-border-color);

    &:hover {
      background-color: var(--bs-light);
    }
  }

  .conversations-section {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem 0;

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem 1rem;
    }

    .section-label {
      font-size: 0.75rem;
      color: var(--bs-secondary);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .clear-history-link {
      font-size: 0.75rem;
      padding: 0;
      color: var(--bs-danger);
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .conversation-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1rem;
    cursor: pointer;
    border-radius: 0;

    &:hover {
      background-color: var(--bs-light);
    }

    &.active {
      background-color: var(--bs-primary-bg-subtle);
    }

    .conversation-title {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 0.875rem;
    }

    .delete-button {
      opacity: 0;
      background: none;
      border: none;
      padding: 0.25rem;
      cursor: pointer;
      color: var(--bs-danger);

      &:hover {
        opacity: 1;
      }
    }

    &:hover .delete-button {
      opacity: 0.7;
    }
  }
`;

const PanelOverlay = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
  z-index: 1040;
`;

const AiChatSidebar = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { showAiChat } = useSelector(
    (state: RootState) => state.all,
    shallowEqual
  );

  // Chat state
  const [conversations, setConversations] = useState<AiChatConversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<AiChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Use the shared AI progress hook
  // progressUpdates contains the current conversation turn (user message, tools, AI response)
  // messages contains persisted history when loading an old conversation
  const {
    sessionId,
    requestId,
    generateRequestId,
    progressUpdates,
    activeTools,
    clearProgress,
  } = useAiProgress({
    onChatComplete: (conversationId) => {
      setIsSending(false);
      // Don't clear progress - keep showing tool executions and final message

      // Update conversation ID if this was a new conversation
      if (conversationId && !currentConversation?.id) {
        setCurrentConversation((prev) => ({
          ...prev,
          id: conversationId,
        } as AiChatConversation));
      }

      // Refresh conversation list to show new/updated conversation
      loadConversationHistory();
    },
    onChatCancelled: () => {
      setIsSending(false);
      // Keep progress to show what happened before cancellation
    },
    onChatError: () => {
      setIsSending(false);
      // Error is now added to progressUpdates so it appears in the correct chronological position
    },
  });

  // State for clear history confirmation dialog
  const [showClearHistoryDialog, setShowClearHistoryDialog] = useState(false);
  // State for conversation panel visibility
  const [isConversationPanelOpen, setIsConversationPanelOpen] = useState(false);

  // Scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, progressUpdates, scrollToBottom]);

  // Load conversation history when sidebar opens
  useEffect(() => {
    if (showAiChat) {
      loadConversationHistory();
    }
  }, [showAiChat]);

  const loadConversationHistory = async () => {
    setIsLoading(true);
    try {
      const { data } = await aiClient.getChatHistory(20);
      setConversations(data || []);
    } catch (error) {
      console.error("Failed to load chat history:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadConversation = async (conversationId: number) => {
    setIsLoading(true);
    setIsSending(false); // Reset sending state when loading a conversation
    clearProgress();
    generateRequestId(); // Generate new requestId for this conversation session
    setIsConversationPanelOpen(false);
    try {
      const { data } = await aiClient.getConversation(conversationId);
      setCurrentConversation(data);
      setMessages(data.messages || []);
    } catch (error) {
      console.error("Failed to load conversation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const startNewChat = () => {
    setCurrentConversation(null);
    setMessages([]);
    setIsSending(false); // Reset sending state when starting new chat
    clearProgress();
    setIsConversationPanelOpen(false);
    inputRef.current?.focus();
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isSending) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    setIsSending(true);
    // Don't clear progress - keep the conversation history visible

    // Only generate a new requestId for new conversations, reuse existing for follow-ups
    const messageRequestId = currentConversation?.id ? requestId : generateRequestId();

    try {
      const { data } = await aiClient.sendChatMessage({
        conversationId: currentConversation?.id,
        message: userMessage,
        webSocketSessionId: sessionId,
        requestId: messageRequestId,
      });

      if (data?.id && !currentConversation?.id) {
        setCurrentConversation((prev) => ({
          ...prev,
          id: data.id,
          title: data.title,
        } as AiChatConversation));
      }
    } catch (error: unknown) {
      console.error("[AiChatSidebar] Failed to send message:", error);
      setIsSending(false);

      // Type guard for axios error
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number; data?: AiError } };

        // Check if this is an AI error with error code
        if (axiosError.response?.status === 400 && axiosError.response?.data) {
          const errorData: AiError | undefined = axiosError.response.data;

          if (errorData?.errorCode) {
            // Add structured error message to chat
            setMessages((prev) => [
              ...prev,
              {
                type: "ERROR",
                text: errorData.message,
                errorCode: errorData.errorCode,
                timestamp: new Date()
              },
            ]);
          } else {
            // Generic error message
            const errorMessage = errorData?.message ||
              (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' ? error.message : undefined) ||
              translate(LanguageKey.ai_chat_error_key);
            setMessages((prev) => [
              ...prev,
              {
                type: "ERROR",
                text: errorMessage,
                errorCode: "UNKNOWN",
                timestamp: new Date()
              },
            ]);
          }
          return;
        }
      }

      // Network or other error
      setMessages((prev) => [
        ...prev,
        {
          type: "ERROR",
          text: translate(LanguageKey.ai_chat_error_key),
          errorCode: "NETWORK_ERROR",
          timestamp: new Date()
        },
      ]);
    }
  };

  const cancelChat = async () => {
    if (sessionId) {
      try {
        await aiClient.cancelChat(sessionId);
      } catch (error) {
        console.error("Failed to cancel chat:", error);
      }
    }
    setIsSending(false);
  };

  const deleteConversation = async (conversationId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await aiClient.deleteConversation(conversationId);
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
      if (currentConversation?.id === conversationId) {
        startNewChat();
      }
    } catch (error) {
      console.error("Failed to delete conversation:", error);
    }
  };

  const clearAllHistory = () => {
    setShowClearHistoryDialog(true);
  };

  const confirmClearAllHistory = async () => {
    setShowClearHistoryDialog(false);
    try {
      await aiClient.clearChatHistory();
      setConversations([]);
      startNewChat();
    } catch (error) {
      console.error("Failed to clear chat history:", error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const onClose = () => {
    dispatch(actions.setShowAiChat(false));
  };

  const getMessageText = (message: ChatMessage): string => {
    if (message.text) return message.text;
    if (message.contents && message.contents.length > 0) {
      return message.contents
        .filter((c) => c.type === "TEXT" && c.text)
        .map((c) => c.text)
        .join("\n");
    }
    return "";
  };

  // Helper to find tool arguments from previous AI messages by tool execution ID
  const findToolArgumentsById = (toolId: string | undefined, currentIndex: number): string | undefined => {
    if (!toolId) return undefined;

    // Search backwards through messages for the matching tool request
    for (let i = currentIndex - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.type === "AI" && msg.toolExecutionRequests) {
        const matchingRequest = msg.toolExecutionRequests.find(req => req.id === toolId);
        if (matchingRequest) {
          return matchingRequest.arguments;
        }
      }
    }
    return undefined;
  };

  // Helper to find tool result from subsequent TOOL_EXECUTION_RESULT messages by tool execution ID
  const findToolResultById = (toolId: string | undefined, currentIndex: number): string | undefined => {
    if (!toolId) return undefined;

    // Search forwards through messages for the matching tool result
    for (let i = currentIndex + 1; i < messages.length; i++) {
      const msg = messages[i];
      if (msg.type === "TOOL_EXECUTION_RESULT" && msg.id === toolId) {
        return getMessageText(msg);
      }
      // Stop searching if we hit another AI message (tool result should come before next AI response)
      if (msg.type === "AI") break;
    }
    return undefined;
  };

  const renderMessage = (message: ChatMessage, index: number) => {
    const text = getMessageText(message);

    // Handle USER messages
    if (message.type === "USER") {
      return (
        <div key={index} className="mb-2">
          <ChatBubble type="user">
            {text}
          </ChatBubble>
        </div>
      );
    }

    // Handle AI messages - may have tool execution requests
    if (message.type === "AI") {
      const hasToolRequests = message.toolExecutionRequests && message.toolExecutionRequests.length > 0;

      // No wrapper margin when tool requests present - ToolExecution handles spacing
      return (
        <div key={index} className={hasToolRequests ? "" : "mb-2"}>
          {/* Render the AI text if present */}
          {text && (
            <ChatBubble type="ai" markdown={text} />
          )}
          {/* Render tool execution requests (started) */}
          {hasToolRequests && message.toolExecutionRequests?.map((tool, toolIdx) => {
            // Look ahead to find the tool result for this request
            const toolResult = findToolResultById(tool.id, index);
            return (
              <ToolExecution
                key={`tool-start-${toolIdx}`}
                toolName={tool.name}
                status="started"
                toolArguments={tool.arguments}
                toolResult={toolResult}
              />
            );
          })}
        </div>
      );
    }

    // Handle TOOL_EXECUTION_RESULT messages
    if (message.type === "TOOL_EXECUTION_RESULT") {
      // Find the matching tool arguments from the AI message that initiated this tool call
      const toolArguments = findToolArgumentsById(message.id, index);

      return (
        <ToolExecution
          key={index}
          toolName={message.toolName || "unknown"}
          status="completed"
          toolArguments={toolArguments}
          toolResult={text}
        />
      );
    }

    // Handle ERROR messages
    if (message.type === "ERROR") {
      return (
        <div key={index} className="mb-2">
          <div
            style={{
              backgroundColor: 'var(--bs-danger-bg-subtle)',
              borderLeft: '4px solid var(--bs-danger)',
              padding: '12px',
              borderRadius: '4px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <span style={{ fontSize: '1.2em' }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                  {translate(LanguageKey.ai_error_title_key) || "AI Error"}
                </div>
                <div>{text}</div>
                {message.errorCode === 'AI_QUOTA_EXCEEDED' && hasPermission(UserPermission.MENU_ADMIN_PLATFORM_PLAN) && (
                  <button
                    type="button"
                    onClick={() => history.push("/product_plan")}
                    style={{
                      color: 'var(--bs-link-color)',
                      marginTop: '8px',
                      display: 'inline-block',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    {translate(LanguageKey.view_quota_page_key) || "View Quota Page"} →
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Handle SYSTEM messages (fallback)
    return (
      <div key={index} className="mb-2">
        <ChatBubble type="system">
          {text}
        </ChatBubble>
      </div>
    );
  };

  const headerLeft = (
    <HeaderButton
      onClick={() => setIsConversationPanelOpen(true)}
      title={translate(LanguageKey.ai_chat_recent_chats_key) || "Recent chats"}
    >
      <AwesomeIcon icon={faBars} />
    </HeaderButton>
  );

  const headerRight = (
    <NewChatButton
      onClick={startNewChat}
      title={translate(LanguageKey.ai_chat_new_chat_key) || "New Chat"}
    >
      <AwesomeIcon icon={faPlus} />
    </NewChatButton>
  );

  return (
    <>
      <FloatingWindow
        show={showAiChat ?? false}
        onClose={onClose}
        title={<strong>AI chat</strong>}
        headerLeft={headerLeft}
        headerRight={headerRight}
      >
        {/* Conversation Panel Overlay */}
        <PanelOverlay
          $isOpen={isConversationPanelOpen}
          onClick={() => setIsConversationPanelOpen(false)}
        />

        {/* Slide-out Conversation Panel */}
        <ConversationPanel $isOpen={isConversationPanelOpen}>
          <div
            className="new-chat-item"
            onClick={startNewChat}
          >
            <AwesomeIcon icon={faPlus} />
            <span>{translate(LanguageKey.ai_chat_new_chat_key) || "New chat"}</span>
          </div>

          <div className="conversations-section">
            <div className="section-header">
              <span className="section-label">
                {translate(LanguageKey.ai_chat_recent_chats_key) || "Recent chats"}
              </span>
              {conversations.length > 0 && (
                <Button
                  variant="link"
                  className="clear-history-link"
                  onClick={clearAllHistory}
                >
                  {translate(LanguageKey.ai_chat_clear_history_key) || "Clear history"}
                </Button>
              )}
            </div>
            {isLoading ? (
              <div className="text-center py-3">
                <Spinner size="sm" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center text-muted py-3">
                {translate(LanguageKey.ai_chat_no_conversations_key) || "No conversations"}
              </div>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv.id}
                  className={`conversation-item ${currentConversation?.id === conv.id ? "active" : ""}`}
                  onClick={() => conv.id && loadConversation(conv.id)}
                >
                  <span className="conversation-title">
                    {conv.title || "Untitled"}
                  </span>
                  <button
                    className="delete-button"
                    onClick={(e) => conv.id && deleteConversation(conv.id, e)}
                  >
                    <SntDeleteIcon size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </ConversationPanel>

        {/* Main Chat Area */}
        <StyledChatContainer>
          <div className="chat-messages">
            {messages.length === 0 && progressUpdates.length === 0 && !isSending && (
              <div className="text-center text-muted py-5">
                <p>{translate(LanguageKey.ai_chat_type_message_key) || "Type a message to start chatting"}</p>
              </div>
            )}

            {messages.map((msg, idx) => renderMessage(msg, idx))}

            <AiProgressUpdates
              updates={progressUpdates}
              activeTools={activeTools}
              autoScroll={false}
            />

            {isSending && (
              <div className="d-flex justify-content-center align-items-center py-3">
                <img
                  alt="Loading"
                  src={AiLoadingIcon}
                  className="ai-loading-spinner"
                  style={{
                    animation: "spin 2s linear infinite",
                    transform: "rotate(0deg)",
                  }}
                />
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            <div className="input-wrapper">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={translate(LanguageKey.ai_chat_type_message_key) || "Type a message..."}
              />
              <div className="input-actions">
                {isSending ? (
                  <button
                    className="stop-button"
                    onClick={cancelChat}
                    title={translate(LanguageKey.cancel_key)}
                  >
                    <AwesomeIcon icon={faStop} />
                  </button>
                ) : (
                  <button
                    className="send-button"
                    onClick={sendMessage}
                    disabled={!inputValue.trim()}
                  >
                    <AwesomeIcon icon={faArrowUp} />
                  </button>
                )}
              </div>
            </div>
            <div className="ai-disclaimer">
              <AiDisclaimer />
            </div>
          </div>
        </StyledChatContainer>
      </FloatingWindow>

      <SntDialog
        isShow={showClearHistoryDialog}
        onSave={confirmClearAllHistory}
        onClose={() => setShowClearHistoryDialog(false)}
        title={translate(LanguageKey.ai_chat_clear_history_key)}
        saveTxt={translate(LanguageKey.delete_key)}
      >
        {() => <div>{translate(LanguageKey.ai_chat_delete_all_conversations_confirm_key)}</div>}
      </SntDialog>
    </>
  );
};

export default AiChatSidebar;
